import React, { useEffect, useLayoutEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TOUR } from "../../constants/testIds/tour";

// Generic, reusable first-visit guided-tour engine. Point it at any
// sequence of steps —
//   { selector, title?, text }         spotlight a specific element, or
//   { text, title? }  (no selector)    a centered, un-highlighted screen
//                                       (used for a closing "you're all
//                                       set" step that isn't about any one
//                                       element)
// — and it finds each target via document.querySelector, darkens
// everything else with four positioned panels around it (so the target
// itself is never covered by any element and stays natively clickable,
// rather than faking clickability through a hole cut in one big overlay),
// and walks a small tooltip alongside it.
//
// State (which step we're on, whether the tour is active at all) is owned
// by the caller via `useTour` so every page can reuse this exact same
// component with its own steps + localStorage key — this component itself
// holds no persistence and knows nothing about any particular page.
const PADDING = 8; // gap between the highlight ring and the target's edge
const GAP = 16; // gap between the highlight and the tooltip
const TOOLTIP_WIDTH = 340;
// Just an estimate used to pick above-vs-below before the tooltip has
// actually rendered (so there's no one-frame flash in the wrong spot) —
// but a real mobile-width tooltip wraps its body text onto noticeably
// more lines than the same copy at TOOLTIP_WIDTH=340, so the estimate has
// to grow on narrow screens or "does it fit below" comes out wrong and
// the tooltip lands overlapping the spotlight ring or the viewport edge.
const EST_TOOLTIP_HEIGHT = 180;
const EST_TOOLTIP_HEIGHT_NARROW = 240; // vw < 480
const FIND_POLL_MS = 150; // how often to re-check for a not-yet-rendered target
const FIND_MAX_ATTEMPTS = 20; // ~3s total — covers a typical data fetch behind a loading skeleton

// A touch target under ~44px is the one iOS/Android accessibility
// guidelines call out by name — matches the min-h-[44px] convention
// already used for tappable buttons elsewhere in this app (Landing.jsx's
// nav-play-first, hero-dashboard, etc.).
const TOUCH_TARGET = "min-h-[44px]";

// window.innerHeight on a phone includes space the browser's own address
// bar/toolbar is currently covering (or not) — it changes as that chrome
// shows and hides on scroll, so a value read once at mount can be taller
// than what's actually visible by the time the tooltip renders, pushing
// it partly off-screen. `visualViewport` tracks the real visible area and
// fires its own resize event on that chrome show/hide, which plain
// `window.resize` does not.
function readViewport() {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    return {
        vw: vv?.width || window.innerWidth,
        vh: vv?.height || window.innerHeight,
    };
}

// How long after first arriving at a step's target the spotlight still
// counts as "traveling there" (full spring glide). Any correction measured
// later than this — a banner finishing its own entrance animation, a slow
// image nudging layout — is treated as settling a late drift rather than a
// deliberate step change, and snaps instead of gliding; see the `instant`
// state below for why that distinction exists at all.
const ARRIVAL_WINDOW_MS = 600;

export default function GuidedTour({ steps, active, onFinish }) {
    const [stepIndex, setStepIndex] = useState(0);
    const [rect, setRect] = useState(null);
    const [instant, setInstant] = useState(false);
    const [viewport, setViewport] = useState(readViewport);

    // Every fresh activation (first visit, or a "Replay Tour" click) starts
    // from step one again.
    useEffect(() => {
        if (active) setStepIndex(0);
    }, [active]);

    useEffect(() => {
        if (!active) return undefined;
        const onResize = () => setViewport(readViewport());
        onResize();
        window.addEventListener("resize", onResize);
        window.visualViewport?.addEventListener("resize", onResize);
        window.visualViewport?.addEventListener("scroll", onResize);
        return () => {
            window.removeEventListener("resize", onResize);
            window.visualViewport?.removeEventListener("resize", onResize);
            window.visualViewport?.removeEventListener("scroll", onResize);
        };
    }, [active]);

    // Locks the page in place for the duration of the tour — without this,
    // wheel/touch/keyboard scrolling still reaches the real page underneath
    // the dark overlay (the overlay darkens and blocks clicks, but doesn't
    // stop scrolling on its own), so a guardian could scroll the spotlight
    // target out from under the tooltip mid-explanation. Restores whatever
    // the page's own overflow was — not assumed to be the default — the
    // moment the tour ends (finish, skip, or unmount) so it can never leak
    // into normal browsing.
    //
    // Also scrolls back to the very top of the page on finish/skip/unmount,
    // not just the overflow — each step's own scrollIntoView leaves the
    // page wherever the last spotlighted target happened to be, and without
    // this the guardian lands back on a partially-scrolled page the moment
    // they finish or skip (seen live: the install banner/live-class bar
    // scrolled out above the top of the viewport, reading as if they'd
    // vanished, until a reload reset the scroll and brought them back).
    //
    // Deliberately (0, 0), not "restore wherever it was when the tour
    // started" (an earlier version of this fix) — that assumption only
    // held for a tour auto-started on mount, where scroll is always 0 at
    // that point anyway. Landing.jsx's "Replay Tour" button is fixed and
    // reachable from any scroll depth, so restoring to "wherever it
    // started" could restore to the middle of the page instead of the top
    // — and every tour in this app (landing/login/onboarding/register)
    // opens on a top-of-page element regardless, so landing back at the
    // literal top is correct for all of them, not just the auto-start case.
    useEffect(() => {
        if (!active) return undefined;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prevOverflow;
            window.scrollTo(0, 0);
        };
    }, [active]);

    const step = steps[stepIndex];
    const hasTarget = Boolean(step?.selector);

    useLayoutEffect(() => {
        if (!active || !step || !hasTarget) {
            setRect(null);
            setInstant(false);
            return undefined;
        }

        let pollTimer;
        let raf;
        let update;
        let cancelled = false;
        let arrivedAt = null;

        // A target can be legitimately absent for two different reasons:
        // still loading behind a skeleton (e.g. the dashboard's poem grid
        // before its data fetch resolves) or genuinely not on the page at
        // this breakpoint/route. There's no way to tell those apart from
        // a selector alone, so briefly poll for it before giving up —
        // long enough to ride out a normal data fetch, short enough that a
        // truly-absent target doesn't stall the tour for long.
        const tryFind = (attempt) => {
            if (cancelled) return;
            const target = document.querySelector(step.selector);
            // A match that exists in the DOM but hasn't actually laid out
            // yet (display:none mid-toggle, a card whose data hasn't
            // painted this frame) measures 0x0 — treating that as "found"
            // collapses the ring to a point for a frame or two, a visible
            // glitch. Fold it into the same poll-and-retry path as a
            // genuinely-missing element rather than accepting it.
            const notReady = !target || (target.offsetWidth === 0 && target.offsetHeight === 0);
            if (notReady) {
                if (attempt >= FIND_MAX_ATTEMPTS) {
                    // Skip straight past it rather than showing a tooltip
                    // pointing at nothing.
                    if (stepIndex < steps.length - 1) setStepIndex((i) => i + 1);
                    else onFinish();
                    return;
                }
                pollTimer = setTimeout(() => tryFind(attempt + 1), FIND_POLL_MS);
                return;
            }

            // Instant, not "smooth" — a smooth scroll animates over several
            // hundred ms, and back-to-back with the previous step's own
            // smooth scroll (still settling when this one starts), the
            // single requestAnimationFrame below used to grab a mid-flight
            // measurement instead of the target's actual resting position —
            // the ring would visibly land on whatever happened to be under
            // it at that moment rather than the real target. Instant scroll
            // removes the race entirely: by the time the next line runs,
            // layout has already settled at its final position.
            target.scrollIntoView({ block: "center", behavior: "auto" });
            let lastRect = null;
            update = () => {
                const r = target.getBoundingClientRect();
                // Skip the setState (and the ring's re-render) when nothing
                // actually moved — this runs every frame for as long as the
                // step is on screen, so it needs to be a no-op in the
                // common case where the page is just sitting still.
                if (lastRect && lastRect.top === r.top && lastRect.left === r.left
                    && lastRect.width === r.width && lastRect.height === r.height) return;
                lastRect = { top: r.top, left: r.left, width: r.width, height: r.height };
                if (arrivedAt === null) arrivedAt = performance.now();
                // First measurement (and anything shortly after it) gets the
                // full glide — that's the "spotlight travels to the new
                // target" motion between steps. A correction measured well
                // after arrival is layout settling underneath a spotlight
                // that already got there, not a step change; gliding those
                // too made the ring visibly crawl for a second or more right
                // when it should already look settled, which read as lag.
                setInstant(performance.now() - arrivedAt > ARRIVAL_WINDOW_MS);
                setRect(lastRect);
            };
            update();
            // Keep re-measuring for as long as this step is showing, not just
            // the first few frames — a sibling element that mounts or
            // resizes late (the install-app banner's delayed fallback, the
            // WhatsApp bubble, a slow image) shifts this target *after* a
            // one-shot settle window would have already stopped watching,
            // leaving the ring frozen at a stale position while the real
            // target drifts away underneath it. window resize/scroll alone
            // don't cover that case — neither fires for a sibling's own
            // layout change — so this loop is the actual fix, not just a
            // safety net.
            const track = () => {
                update();
                raf = requestAnimationFrame(track);
            };
            raf = requestAnimationFrame(track);
            window.addEventListener("resize", update);
            window.addEventListener("scroll", update, true);
        };
        tryFind(0);

        return () => {
            cancelled = true;
            if (pollTimer) clearTimeout(pollTimer);
            if (raf) cancelAnimationFrame(raf);
            if (update) {
                window.removeEventListener("resize", update);
                window.removeEventListener("scroll", update, true);
            }
        };
    }, [active, step, hasTarget, stepIndex, steps.length, onFinish]);

    if (!active || !step) return null;
    if (hasTarget && !rect) return null; // still measuring the target

    const isLast = stepIndex === steps.length - 1;
    const handleNext = () => (isLast ? onFinish() : setStepIndex((i) => i + 1));
    const handleBack = () => setStepIndex((i) => Math.max(0, i - 1));

    const { vw, vh } = viewport;
    const tooltipWidth = Math.min(TOOLTIP_WIDTH, vw - 32);
    const estTooltipHeight = vw < 480 ? EST_TOOLTIP_HEIGHT_NARROW : EST_TOOLTIP_HEIGHT;

    let tooltipTop;
    let tooltipLeft;
    let holeTop = 0;
    let holeLeft = 0;
    let holeWidth = 0;
    let holeHeight = 0;

    if (hasTarget) {
        holeTop = Math.max(rect.top - PADDING, 0);
        holeLeft = Math.max(rect.left - PADDING, 0);
        holeWidth = rect.width + PADDING * 2;
        holeHeight = rect.height + PADDING * 2;

        if (holeTop + holeHeight + GAP + estTooltipHeight <= vh) {
            tooltipTop = holeTop + holeHeight + GAP; // below the target
        } else if (holeTop - GAP - estTooltipHeight >= 0) {
            tooltipTop = holeTop - GAP - estTooltipHeight; // above it
        } else {
            tooltipTop = Math.max(vh - estTooltipHeight - 16, 16); // neither fits — pin low
        }
        tooltipLeft = Math.min(Math.max(holeLeft + holeWidth / 2 - tooltipWidth / 2, 16), vw - tooltipWidth - 16);
    } else {
        tooltipTop = Math.max((vh - estTooltipHeight) / 2, 16);
        tooltipLeft = (vw - tooltipWidth) / 2;
    }

    const panelClass = "fixed bg-charcoal/70";
    // Spring, not a fixed duration — a duration-based tween moving between
    // two very different distances (a short hop between two nearby cards
    // vs. a long scroll-and-jump to the library grid) either looks
    // sluggish on the short move or snaps too fast on the long one; a
    // spring's travel time scales with distance on its own, so both read
    // as the same "weight" of motion. Tuned snappier than a default spring
    // (higher stiffness, tighter damping) — the original 300/30 pairing
    // read as a slow drag on a big cross-page jump (e.g. the poem grid to
    // a corner button), not a deliberate "look here" motion.
    const glideTransition = { type: "spring", stiffness: 500, damping: 40 };
    // A late same-step correction (see ARRIVAL_WINDOW_MS) isn't "travel" at
    // all — it's the spotlight catching up to a target that moved out from
    // under it after arrival. Snapping it fast rather than re-gliding is
    // what actually fixes that lag; a spring here would just crawl again.
    const instantTransition = { type: "tween", duration: 0.12, ease: "easeOut" };
    const holeTransition = instant ? instantTransition : glideTransition;
    // The tooltip's top/left are plain React-computed style values, not an
    // animated motion value — the instant `rect` updates to (i.e. the
    // ring/panels' actual glide destination), so without a delay it would
    // pop into place immediately while the ring is still ~500ms into
    // physically traveling there, two halves of one "move to the next
    // thing" motion visibly finishing at completely different times. A late
    // same-step correction (`instant`) isn't a step change at all, so it
    // gets none of this — the tooltip's already sitting there and has no
    // reason to hide itself again.
    const tooltipDelay = instant ? 0 : 0.22;

    return (
        <motion.div
            data-testid={TOUR.overlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25 }}
        >
            {hasTarget ? (
                <>
                    {/* One seamless overlay with a true cut-out hole (SVG
                        mask), not 4 separately-positioned rectangles framing
                        the target. Four independent panels can never be
                        made perfectly seamless against each other — sized
                        exactly, a one-frame rounding mismatch flashes a
                        hairline of the real page through the gap; sized
                        with a deliberate overlap ("bleed") to hide that,
                        the overlap itself double-paints the same
                        semi-transparent color darker than the rest, which
                        showed up as a visible line running the full width
                        and height of the screen at the hole's edges. A
                        mask has no seams to begin with — it's one shape,
                        rendered once, so neither failure mode can happen. */}
                    <svg className="fixed inset-0 pointer-events-none" width={vw} height={vh} style={{ zIndex: 9998 }}>
                        <defs>
                            <mask id="tour-spotlight-mask">
                                <rect x={0} y={0} width={vw} height={vh} fill="white" />
                                <motion.rect
                                    initial={false}
                                    animate={{ x: holeLeft, y: holeTop, width: holeWidth, height: holeHeight }}
                                    transition={holeTransition}
                                    rx={16}
                                    fill="black"
                                />
                            </mask>
                        </defs>
                        <rect x={0} y={0} width={vw} height={vh} fill="rgba(44,58,53,0.7)" mask="url(#tour-spotlight-mask)" />
                    </svg>

                    {/* The mask above is purely visual (pointer-events:none
                        on the whole svg) — these 4 plain, invisible panels
                        are what actually block clicks/scroll outside the
                        target. Nothing here is ever seen, so the same
                        seam concerns the mask was built to avoid simply
                        don't apply to this layer at all. */}
                    <motion.div className="fixed pointer-events-auto" initial={false} animate={{ top: 0, left: 0, width: "100%", height: holeTop }} transition={holeTransition} style={{ zIndex: 9998 }} />
                    <motion.div className="fixed pointer-events-auto" initial={false} animate={{ top: holeTop + holeHeight, left: 0, width: "100%", height: Math.max(vh - (holeTop + holeHeight), 0) }} transition={holeTransition} style={{ zIndex: 9998 }} />
                    <motion.div className="fixed pointer-events-auto" initial={false} animate={{ top: holeTop, left: 0, width: holeLeft, height: holeHeight }} transition={holeTransition} style={{ zIndex: 9998 }} />
                    <motion.div className="fixed pointer-events-auto" initial={false} animate={{ top: holeTop, left: holeLeft + holeWidth, width: Math.max(vw - (holeLeft + holeWidth), 0), height: holeHeight }} transition={holeTransition} style={{ zIndex: 9998 }} />

                    {/* Used to also draw a ring-4 ring-brightGreen border
                        around the hole here, as its own separately-animated
                        motion.div — exactly the "two different animation
                        engines chasing the same rectangle" desync risk this
                        file already builds around elsewhere, just applied
                        to the ring instead of the panels: on a fast glide
                        between steps the ring's own div could paint a frame
                        or two off from the mask's own cutout, showing up as
                        a stray green line trailing or leading the actual
                        spotlight. Removed rather than re-synced — the dark
                        overlay's own cutout already makes the target
                        unambiguous without a second colored outline drawn
                        on top of it. */}
                </>
            ) : (
                <div className={panelClass} style={{ top: 0, left: 0, width: "100%", height: "100%", zIndex: 9998 }} />
            )}

            {/* No `mode="wait"` — the outgoing tooltip and the incoming one
                are allowed to overlap (a quick crossfade) instead of the
                outgoing one fully finishing its exit before the next one
                starts, which left a brief blank gap where nothing was
                visible at all between steps. */}
            <AnimatePresence>
                <motion.div
                    key={stepIndex}
                    initial={{ opacity: 0, y: 10, scale: 0.96 }}
                    // Delayed to roughly land once the spotlight has actually
                    // arrived (see tooltipDelay above) — without this the
                    // tooltip pops into place at its final spot instantly
                    // while the ring is still ~500ms into gliding there, two
                    // halves of the same motion finishing at visibly
                    // different times.
                    animate={{ opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 340, damping: 28, delay: tooltipDelay } }}
                    // Exit stays immediate and quick regardless of the
                    // arrival delay above — it's leaving, not arriving.
                    exit={{ opacity: 0, y: -6, scale: 0.98, transition: { duration: 0.15 } }}
                    className="fixed rounded-2xl bg-white shadow-2xl shadow-black/30 border-2 border-honeySoft p-5"
                    style={{ top: tooltipTop, left: tooltipLeft, width: tooltipWidth, zIndex: 10000 }}
                    role="dialog"
                    aria-live="polite"
                    data-testid={TOUR.tooltip}
                >
                    <p className="text-xs font-bold text-brightGreen uppercase tracking-wider mb-1">
                        Step {stepIndex + 1} of {steps.length}
                    </p>
                    {step.title && <h3 className="font-display font-bold text-lg text-deepGreen mb-1">{step.title}</h3>}
                    <p className="text-sm text-charcoal leading-relaxed">{step.text}</p>

                    <div className="mt-4 flex items-center justify-between gap-2">
                        <button
                            type="button"
                            onClick={onFinish}
                            className={`text-xs font-semibold text-mocha hover:text-charcoal underline px-1 flex items-center ${TOUCH_TARGET}`}
                            data-testid={TOUR.skipButton}
                        >
                            Skip tour
                        </button>
                        <div className="flex items-center gap-2">
                            {stepIndex > 0 && (
                                <motion.button
                                    whileTap={{ scale: 0.95 }}
                                    type="button"
                                    onClick={handleBack}
                                    className={`px-4 py-2 rounded-full text-sm font-semibold text-deepGreen border-2 border-honeySoft hover:border-brightGreen transition flex items-center ${TOUCH_TARGET}`}
                                    data-testid={TOUR.backButton}
                                >
                                    Back
                                </motion.button>
                            )}
                            <motion.button
                                whileTap={{ scale: 0.95 }}
                                type="button"
                                onClick={handleNext}
                                className={`px-5 py-2 rounded-full text-sm font-bold text-white bg-brightGreen hover:bg-deepGreen transition flex items-center ${TOUCH_TARGET}`}
                                data-testid={isLast ? TOUR.finishButton : TOUR.nextButton}
                            >
                                {isLast ? "Finish" : "Next"}
                            </motion.button>
                        </div>
                    </div>
                </motion.div>
            </AnimatePresence>
        </motion.div>
    );
}
